<script setup>
import {ref, watch, computed, onMounted} from "vue";
import {endpoints} from "~/network/endpoints";
import {useRoute, useRouter} from "vue-router";
import {useMailer} from "~/composables/useMailer";
import PlayerCard from "~/components/players/PlayerCard.vue";
import TiptapEditor from "~/components/fields/TiptapEditor.vue";
import {whenHydrated} from "~/utils/queryFlags";

const route = useRoute();
const router = useRouter();
const filtersQuery = computed(() => route.query.mailerQuery);
// X44 (R20 M2): ?mailerQuery=… on a direct load must not open the dialog during hydration (Vuetify's overlay
// throws "reading 'classList'" and the page is a 500); it opens once hydrated.
const nuxtApp = useNuxtApp();
const hydrated = ref(false);
onMounted(() => { whenHydrated(() => !!nuxtApp.isHydrating, () => { hydrated.value = true; }); });

const {items: users, getItems: getUsers, listPending: loadingUsers, totalItems: usersTotal} = useCrudApi("api/v2/users");
// Club members matching the same selection — members without an account (or
// without an email) can't be mailed, so the gap is shown as a note.
const {getItems: getMembers, totalItems: membersTotal, listPending: loadingMembers} = useCrudApi("api/v2/players");
const memberCountKnown = ref(false);

const {message, subject, sendLoading, sendErrors, sendResult, sendMail, filterQueryToFilters} = useMailer();


//Computed ref based on route query
const dialogOpen = computed({ // getter and setter
  get() {
    return hydrated.value && !!route.query.mailerQuery;
  },
  set(value) {
    if (value === false) {
      if (router.options.history.state.back) {
        router.back();
      } else {
        let newQuery = {...route.query};
        delete newQuery.mailerQuery;
        router.replace({...route, query: newQuery});
      }
    }
  }
});

// Only the club-wide selection and an explicit player list map 1:1 onto club
// members; competition selections have no equivalent members count.
const memberFiltersFor = (filters) => {
  const keys = Object.keys(filters).filter((k) => k !== 'fromWebSearch' && filters[k] != null);
  if (keys.length === 1 && keys[0] === 'clubIds') return {clubIds: filters.clubIds};
  if (keys.length === 1 && keys[0] === 'playerIds' && Array.isArray(filters.playerIds)) return {ids: filters.playerIds};
  // D48: the Players page's filters (same names on users and players) for one club.
  const playerFilterKeys = ['clubIds', 'keywords', 'membership', 'membershipTypeIds', 'membershipStatuses', 'inGroup'];
  if (keys.includes('clubIds') && keys.every(k => playerFilterKeys.includes(k))) {
    return Object.fromEntries(keys.map(k => [k, filters[k]]));
  }
  return null;
};

const loadRecipients = async () => {
  sendResult.value = {};
  memberCountKnown.value = false;
  const filtersAre = filterQueryToFilters(filtersQuery.value);
  getUsers(1, 100, filtersAre);
  const memberFilters = memberFiltersFor(filtersAre);
  if (memberFilters) {
    try {
      await getMembers(1, 1, memberFilters);
      memberCountKnown.value = true;
    } catch (e) {
      memberCountKnown.value = false;
    }
  }
};

onMounted(() => {
  if (dialogOpen.value) loadRecipients();
});

watch(dialogOpen, (newOpen) => {
  if (newOpen) loadRecipients();
});

// The list shows at most the first 100 names; the send goes to every match.
const recipientCount = computed(() => Math.max(usersTotal.value || 0, users.value?.length ?? 0));
const recipientLabel = computed(() => `${recipientCount.value} recipient${recipientCount.value === 1 ? '' : 's'}`);
const hiddenRecipientCount = computed(() => Math.max(0, recipientCount.value - (users.value?.length ?? 0)));
const unreachableCount = computed(() => {
  if (!memberCountKnown.value || loadingMembers.value) return 0;
  return Math.max(0, (membersTotal.value || 0) - recipientCount.value);
});
const dummyModel = ref({});

const send = async () => {
  if (!subject.value || !message.value) {
    sendErrors.value = {
      subject: !subject.value ? "Subject is required" : null,
      message: !message.value ? "Message is required" : null
    };
    return;
  }
  await sendMail(filterQueryToFilters(filtersQuery.value));
  if (sendResult.value.id) {
    dialogOpen.value = false; // Close the dialog on successful send
  }
};

const hasNoRecipients = computed(() => {
  return !loadingUsers.value && users.value && recipientCount.value === 0;
});

const shouldShowRefinement = computed(() => {
  const filterValues = filterQueryToFilters(filtersQuery.value);
  return !filterValues.leagueCycleDivisionIds && !filterValues.leagueCycleIds && !filterValues.ladderIds && !filterValues.americanoIds && !filterValues.meetIds && !filterValues.playerIds;
});

</script>

<template>
  <crud-form-dialog
      v-model:dialog="dialogOpen"
      v-model="dummyModel"
      :form-errors="sendErrors"
      :save-action="send"
      :disabled="hasNoRecipients || loadingUsers"
      :form-pending="sendLoading"
      create-title="Send mail"
      action="create"
      :btn-text="loadingUsers ? 'Loading recipients...' : 'Send mail to ' + recipientLabel"
      btn-icon="mdi-send"
  >
    <template #default>
      <div class="tl-card pa-5 text-center" v-if="hasNoRecipients">
        <div>
          <v-icon icon="mdi-account-cancel" size="48" class="mb-3"></v-icon>
        </div>
        <h3>No recipients found</h3>
        <div v-if="unreachableCount > 0" class="text-body-2 mt-2">
          {{ unreachableCount }} player{{ unreachableCount === 1 ? ' has' : 's have' }} no email or account, so
          can't be emailed.
        </div>
      </div>
      <div v-else-if="!loadingUsers">
        <div class="tl-card pa-2 mb-3">
          <strong>{{ loadingUsers ? 'Loading recipients' : recipientLabel }}:</strong>
          <v-chip-group column>
            <v-chip size="x-small" v-for="user in users" :key="user.id" class="ma-1 tl-chip" variant="outlined">
              {{ user.fullName }}
            </v-chip>
            <v-chip v-if="hiddenRecipientCount > 0" size="x-small" class="ma-1 tl-chip" variant="flat">
              and {{ hiddenRecipientCount }} more
            </v-chip>
          </v-chip-group>
          <div v-if="unreachableCount > 0" class="text-caption mt-1">
            <v-icon size="14" class="mr-1">mdi-email-off-outline</v-icon>
            {{ unreachableCount }} player{{ unreachableCount === 1 ? ' has' : 's have' }} no email or account and
            won't receive this.
          </div>
          <div v-if="shouldShowRefinement">
            <v-menu>
              <template #activator="{props}">
                <v-chip size="small" v-bind="props" class="tl-chip tl-chip--brand">
                  <v-icon icon="mdi-filter" class="mr-1"></v-icon>
                  Need a more refined selection?</v-chip>
              </template>
              <div class="tl-card pa-4 font-weight-bold" style="max-width: 300px;">
                Navigate to the individual box league or ladder pages for more options to email smaller groups of players.
              </div>
            </v-menu>
          </div>
        </div>
        <div class="tl-label mb-2">Subject</div>
        <v-text-field v-model="subject" :error="sendErrors.subject" :error-messages="sendErrors.subject"
                      outlined/>
        <tiptap-editor :disable-uploads="true" v-model="message"/>

        <crud-error-display :errors="sendErrors"/>
      </div>
      <div v-else-if="loadingUsers">
        <v-skeleton-loader content="chip" height="60" class="mb-2"/>
        <v-skeleton-loader content="chip" height="50" class="mb-2"/>
        <v-skeleton-loader content="chip" height="300"/>
      </div>

    </template>
  </crud-form-dialog>
</template>

<style scoped lang="scss">

</style>